import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Card, CircularProgress, Skeleton, keyframes, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
// import { motion, AnimatePresence } from 'framer-motion';
// Import from hooks with explicit path
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useStaggerAnimation from '../../hooks/useScrollAnimation';
import useCounterAnimation from '../../hooks/useScrollAnimation';
import useMagneticEffect from '../../hooks/useScrollAnimation';
import useParallax from '../../hooks/useScrollAnimation';

/**
 * Animated Container with scroll trigger
 */
export const AnimatedContainer = ({ 
  children, 
  animation = 'fade-in', 
  delay = 0, 
  duration = 0.6,
  ...props 
}) => {
  const { ref, isVisible } = useScrollAnimation();

  const animationClasses = {
    'fade-in': 'scroll-fade-in',
    'slide-left': 'scroll-slide-left',
    'slide-right': 'scroll-slide-right',
    'slide-up': 'slide-up'
  };

  return (
    <Box
      ref={ref}
      className={`${animationClasses[animation]} ${isVisible ? 'in-view' : ''}`}
      sx={{
        transitionDelay: `${delay}ms`,
        transitionDuration: `${duration}s`,
        ...props.sx
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

/**
 * Animated Button with micro-animations
 */
export const AnimatedButton = ({ 
  children, 
  magnetic = false, 
  ripple = true,
  variant = 'contained',
  ...props 
}) => {
  const magneticProps = useMagneticEffect(0.2);
  const magneticPropsToUse = magnetic ? magneticProps : {};
  
  return (
    <Button
      {...magneticPropsToUse}
      className={`btn-micro ${magnetic ? 'magnetic-btn' : ''} ${props.className || ''}`}
      variant={variant}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...magneticPropsToUse.style,
        ...props.sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Enhanced Animated Card with Framer Motion
 */
export const AnimatedCard = ({
  children,
  index = 0,
  stagger = true,
  hover = true,
  ...props
}) => {
  const theme = useTheme();

  return (
    <div
      style={{
        borderRadius: '16px',
        overflow: 'hidden'
      }}
    >
      <Card
        sx={{
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[2],
          ...props.sx
        }}
        {...props}
      >
        {children}
      </Card>
    </div>
  );
};

/**
 * Staggered List Animation
 */
export const StaggeredList = ({ children, delay = 100, className = '' }) => {
  const { ref, isVisible } = useScrollAnimation();
  const [startAnimation, setStartAnimation] = useState(false);

  useEffect(() => {
    if (isVisible && !startAnimation) {
      setStartAnimation(true);
    }
  }, [isVisible, startAnimation]);

  return (
    <Box ref={ref} className={className}>
      {React.Children.map(children, (child, index) => (
        <Box
          key={index}
          className={`stagger-item ${startAnimation ? 'animate' : ''}`}
          sx={{
            transitionDelay: `${index * delay}ms`
          }}
        >
          {child}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Animated Counter
 */
export const AnimatedCounter = ({ 
  end, 
  duration = 2000, 
  start = 0, 
  suffix = '',
  prefix = '',
  ...props 
}) => {
  const { ref, isVisible } = useScrollAnimation();
  const { count, startCounter } = useCounterAnimation(end, duration, start);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (isVisible && !hasStarted) {
      startCounter();
      setHasStarted(true);
    }
  }, [isVisible, hasStarted, startCounter]);

  return (
    <Typography
      ref={ref}
      className={`counter ${isVisible ? 'animate' : ''}`}
      {...props}
    >
      {prefix}{count.toLocaleString()}{suffix}
    </Typography>
  );
};

/**
 * Text Reveal Animation
 */
export const TextReveal = ({ 
  children, 
  tag = 'div', 
  stagger = true,
  ...props 
}) => {
  const { ref, isVisible } = useScrollAnimation();
  const Component = tag;
  
  // Check if children is a string or contains JSX elements
  if (typeof children !== 'string') {
    // For JSX children (like with <br />), apply animation to the whole content
    return (
      <Component
        ref={ref}
        className={`text-reveal ${isVisible ? 'animate' : ''}`}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s ease',
        }}
        {...props}
      >
        {children}
      </Component>
    );
  }
  
  // For string children, split into words and animate each
  const words = children.split(' ');

  return (
    <Component
      ref={ref}
      className={`text-reveal ${isVisible ? 'animate' : ''}`}
      {...props}
    >
      {words.map((word, index) => (
        <span
          key={index}
          style={{
            transitionDelay: stagger ? `${index * 0.1}s` : '0s'
          }}
        >
          {word}&nbsp;
        </span>
      ))}
    </Component>
  );
};

/**
 * Parallax Container
 */
export const ParallaxContainer = ({ 
  children, 
  speed = 0.5, 
  className = '',
  ...props 
}) => {
  const { ref, style } = useParallax(speed);

  return (
    <Box
      ref={ref}
      className={`parallax-element ${className}`}
      sx={{
        ...style,
        willChange: 'transform',
        ...props.sx
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

/**
 * Loading Skeleton with wave animation
 */
export const AnimatedSkeleton = ({ 
  width = '100%', 
  height = 20, 
  variant = 'rectangular',
  ...props 
}) => {
  return (
    <Box
      className="skeleton"
      sx={{
        width,
        height,
        borderRadius: variant === 'circular' ? '50%' : '8px',
        ...props.sx
      }}
      {...props}
    />
  );
};

/**
 * Progress Bar with smooth animation
 */
export const AnimatedProgress = ({ 
  value = 0, 
  animated = true, 
  ...props 
}) => {
  const theme = useTheme();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (animated) {
      const timer = setTimeout(() => {
        setProgress(value);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setProgress(value);
    }
  }, [value, animated]);

  return (
    <Box
      sx={{
        width: '100%',
        height: 4,
        backgroundColor: 'rgba(255, 140, 66, 0.2)',
        borderRadius: 2,
        overflow: 'hidden',
        ...props.sx
      }}
    >
      <Box
        sx={{
          width: `${progress}%`,
          height: '100%',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          borderRadius: 2,
          transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </Box>
  );
};

/**
 * Floating Action Button with animations
 */
export const FloatingActionButton = ({ 
  children, 
  onClick, 
  className = '',
  ...props 
}) => {
  const theme = useTheme();
  const [isPressed, setIsPressed] = useState(false);

  return (
    <Button
      className={`fab ${className} ${isPressed ? 'pressed' : ''}`}
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 56,
        height: 56,
        borderRadius: '50%',
        minWidth: 'unset',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
        zIndex: 1000,
        ...props.sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Morphing Icon with smooth transitions
 */
export const MorphingIcon = ({ 
  icon1, 
  icon2, 
  isToggled = false, 
  size = 24,
  ...props 
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        ...props.sx
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: isToggled ? 0 : 1,
          transform: isToggled ? 'rotate(90deg) scale(0.8)' : 'rotate(0deg) scale(1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {icon1}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: isToggled ? 1 : 0,
          transform: isToggled ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.8)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {icon2}
      </Box>
    </Box>
  );
};

/**
 * Notification with slide animation
 */
export const AnimatedNotification = ({ 
  children, 
  visible = false, 
  position = 'top-right',
  ...props 
}) => {
  const positions = {
    'top-right': { top: 24, right: 24 },
    'top-left': { top: 24, left: 24 },
    'bottom-right': { bottom: 24, right: 24 },
    'bottom-left': { bottom: 24, left: 24 }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        ...positions[position],
        transform: visible ? 'translateX(0) scale(1)' : 'translateX(300px) scale(0.8)',
        opacity: visible ? 1 : 0,
        transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
        zIndex: 1500,
        ...props.sx
      }}
    >
      {children}
    </Box>
  );
};

// Floating animation keyframe
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// Enhanced loading component with floating animation
export const FloatingLoader = ({ size = 40, color = 'primary' }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 120
      }}
    >
      <CircularProgress size={size} color={color} thickness={2.5} />
    </Box>
  );
};

// Skeleton loading with wave effect
export const SkeletonCard = ({ height = 200 }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 2, mb: 2 }} animation="wave" />
      <Skeleton variant="text" height={30} sx={{ mb: 1 }} animation="wave" />
      <Skeleton variant="text" height={20} width="60%" animation="wave" />
    </Box>
  );
};

// Page transition wrapper (no animation)
export const PageTransition = ({ children, ...props }) => {
  return (
    <div {...props}>
      {children}
    </div>
  );
};

// Enhanced floating button (no animation)
export const EnhancedFloatingButton = ({ children, ...props }) => {
  return (
    <div
      style={{ borderRadius: '50%' }}
      {...props}
    >
      {children}
    </div>
  );
};

export default {
  AnimatedContainer,
  AnimatedButton,
  AnimatedCard,
  StaggeredList,
  AnimatedCounter,
  TextReveal,
  ParallaxContainer,
  AnimatedSkeleton,
  AnimatedProgress,
  FloatingActionButton,
  MorphingIcon,
  AnimatedNotification,
  FloatingLoader,
  SkeletonCard,
  PageTransition,
  EnhancedFloatingButton
};